const DEFAULT_PRECISION = 2;

/**
 * Replace `{path}` and `{path:N}` placeholders with resolved values,
 * while preserving content inside `$...$` LaTeX delimiters.
 *
 * All placeholders are resolved via the callback — there are no reserved
 * names (unlike the label-specific `formatTemplate` which handles `{name}`,
 * `{x}`, `{deg}`, etc. via `LabelContext`).
 *
 * @param template - Text containing `{path}` placeholders
 * @param resolveValue - Callback to resolve a path to a value
 * @param defaultPrecision - Default decimal places for numbers (default: 2)
 * @returns Text with placeholders replaced by resolved values
 */
export function interpolateValues(
  template: string,
  resolveValue: (path: string) => number | string | undefined,
  defaultPrecision?: number,
): string {
  if (!template.includes('{')) {
    return template;
  }

  // Split into LaTeX segments ($...$) and plain text segments.
  // Only interpolate placeholders in plain text segments.
  const parts = template.split(/(?<!\\)\$(.+?)(?<!\\)\$/g);
  // parts alternates: [plainText, latexContent, plainText, latexContent, ...]

  for (let i = 0; i < parts.length; i++) {
    // Even indices are plain text, odd indices are LaTeX content
    if (i % 2 === 0) {
      parts[i] = parts[i].replace(/\{([^:}]+)(?::(\d+))?\}/g, (match, path: string, precStr?: string) => {
        const value = resolveValue(path);
        if (value === undefined || (typeof value === 'number' && isNaN(value))) {
          return '\u2014'; // em dash fallback
        }
        if (typeof value === 'string') {
          return value;
        }
        const precision = precStr !== undefined ? parseInt(precStr, 10) : defaultPrecision ?? DEFAULT_PRECISION;
        return value.toFixed(precision);
      });
    } else {
      // Re-wrap LaTeX content with $ delimiters
      parts[i] = `$${parts[i]}$`;
    }
  }

  return parts.join('');
}
